import { ReactNode, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AxiosError } from "axios";
import { CssBaseline, ThemeProvider } from "@mui/material";

import { useAppSelector, useAppDispatch } from "./app/hooks";
import {
  clearToken,
  defaultAuthState,
  loadAuthState,
  setAuthState,
  getToken,
} from "./app/auth";
import { createClient, HttpClientContext, X_REQUEST_ID } from "./app/client";
import {
  getThemeKind,
  getTheme,
  ThemeKind,
  setThemePref,
  ThemeKindContext,
} from "./app/theme";

import Loading from "./components/common/Loading";
import Error from "./components/common/Error";
import Lost from "./components/common/Lost";
import Login from "./components/auth/Login";
import LoginRedirect from "./components/auth/LoginRedirect";
import Logout from "./components/auth/Logout";
import Layout from "./components/layout/Layout";

import Home from "./components/Home";
import Admin, { AdminPage } from "./components/admin/Admin";
import ItemsLoader from "./components/common/items/ItemsLoader";
import ItemForm from "./components/common/items/ItemForm";
import Tool from "./components/tools/Tool";
import Requests from "./components/Requests";
import {
  AutomationConfig,
  ExtConfig,
  HooksConfig,
  Role,
  ScheduleConfig,
  ToolConfig,
  User,
} from "./app/backendTypes";
import { setEmployeesState } from "./app/store";
import Approvals from "./components/admin/Approvals";
import Users from "./components/admin/Users";
import Employees from "./components/employees/Employees";
import Profile from "./components/Profile";
import Tools from "./components/tools/Tools";
import Contracts from "./components/contracts/Contracts";
import Contract from "./components/contracts/Contract";
import Roles from "./components/admin/Roles";
import Tasks from "./components/admin/Tasks";
import Logs from "./components/admin/Logs";
import Data from "./components/admin/Data";
import Datasets from "./components/datasets/Datasets";
import Actions from "./components/admin/Actions";
import Catalog from "./components/catalog/Catalog";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Metrics from "./components/admin/Metrics";

function App() {
  const [error, setError] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [themeKind, setThemeKind] = useState(getThemeKind());

  const auth = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();

  useEffect(() => {
    loadAuthState()
      .then((val) => {
        dispatch(setAuthState(val));
      })
      .catch((e: AxiosError) => {
        if (e.response?.status === 404) {
          clearToken();
          dispatch(setAuthState(defaultAuthState()));
        } else setError(e.response?.headers[X_REQUEST_ID]);
      })
      .finally(() => setLoading(false));
  }, [dispatch]);

  const client = createClient(getToken());

  useEffect(() => {
    if (auth.userId === null) return;

    client
      ?.get<User[]>(`/api/users/${auth.userId}/employees`)
      .then((res) => dispatch(setEmployeesState(res.data)));
  }, [auth.userId, client, dispatch]);

  const hasEmployees = useAppSelector(
    (state) => state.employees.items.length > 0
  );

  const theme = getTheme(themeKind);

  let routes;

  if (loading) routes = defaultRoutes(<Loading />);
  else if (error) routes = defaultRoutes(<Error error={error} />);
  else if (auth.loggedIn)
    routes = loggedInRoutes(auth.roles, auth.userId, hasEmployees);
  else routes = defaultRoutes(<LoginRedirect reason={auth.reason} />);

  return (
    <ThemeKindContext.Provider
      value={{
        kind: themeKind,
        setThemeKind: (kind: ThemeKind) => {
          setThemePref(kind);
          setThemeKind(kind);
        },
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <HttpClientContext.Provider value={client}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <BrowserRouter>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route path="login" element={<Login />} />
                  <Route path="logout" element={<Logout />} />
                  {routes}
                </Route>
              </Routes>
            </BrowserRouter>
          </LocalizationProvider>
        </HttpClientContext.Provider>
      </ThemeProvider>
    </ThemeKindContext.Provider>
  );
}

export default App;

function loggedInRoutes(
  roles: Role[],
  userId: number | null,
  hasEmployees: boolean
) {
  let adminRoutes = null;

  if (roles.includes(Role.Security)) {
    adminRoutes = (
      <>
        <Route path="admin">
          {indexRoute(<Admin />)}
          {wildcardRoute(<Lost />)}
          <Route path={AdminPage.Users} element={<Users />} />
        </Route>
      </>
    );
  }
  if (roles.includes(Role.Administrator)) {
    const isDeveloper = roles.includes(Role.Developer);

    adminRoutes = (
      <>
        <Route path="admin">
          {indexRoute(<Admin />)}
          {wildcardRoute(<Lost />)}
          {adminExtRoutes()}
          {adminAutomationsRoutes()}
          {adminToolsRoutes()}
          {adminApprovalsRoutes()}
          {adminUsersRoutes()}
          {adminScheduleRoutes()}
          {adminRolesRoutes()}
          {adminTasksRoutes()}
          {adminLogsRoutes()}
          {adminHooksRoutes()}
          {adminActionsRoutes()}
          {adminMetricsRoutes()}

          {isDeveloper && adminDataRoutes()}
        </Route>
      </>
    );
  }

  return (
    <>
      {indexRoute(<Home />)}
      {wildcardRoute(<Lost />)}
      {adminRoutes}
      {toolsRoutes()}
      {requestsRoutes(userId)}
      {hasEmployees && employeesRoutes()}
      {contractsRoutes()}
      {datasetsRoutes()}
      {catalogRoutes()}
      {internalRoutes()}
      <Route path="profile" element={<Profile />} />
    </>
  );
}

function datasetsRoutes() {
  return (
    <>
      <Route path="datasets" element={<Datasets />} />
      <Route path="datasets/:id" element={<Datasets />} />
    </>
  );
}

function contractsRoutes() {
  return (
    <>
      <Route path="contracts" element={<Contracts />} />
      <Route path="contracts/:id" element={<Contract />} />
    </>
  );
}

function requestsRoutes(userId: number | null) {
  if (userId === null) return <></>;

  return <Route path="requests" element={<Requests userId={userId} />} />;
}

function toolsRoutes() {
  return (
    <>
      <Route path="tools" element={<Tools />} />
      <Route
        path="tools/:item"
        element={
          <ItemsLoader
            variant="tools"
            render={(config) => <Tool config={config as ToolConfig} />}
          />
        }
      />
    </>
  );
}

function employeesRoutes() {
  return <Route path="employees" element={<Employees />} />;
}

function catalogRoutes() {
  return (
    <>
      <Route path="catalog/:id" element={<Catalog />} />
      <Route path="catalog" element={<Catalog />} />
    </>
  );
}

function internalRoutes() {
  return (
    <>
      <Route path="internal/list_tools" element={<Lost />} />
      <Route path="internal/request_access" element={<Lost />} />
      <Route path="internal/manage_approval_response" element={<Lost />} />

    </>
  );
}

function adminMetricsRoutes() {
  const metrics = AdminPage.Metrics;

  return <Route path={metrics} element={<Metrics />} />;
}

function adminDataRoutes() {
  const data = AdminPage.Data;

  return <Route path={data} element={<Data />} />;
}

function adminActionsRoutes() {
  const actions = AdminPage.Actions;

  return <Route path={actions} element={<Actions />} />;
}

function adminLogsRoutes() {
  const logs = AdminPage.Logs;

  return <Route path={logs} element={<Logs />} />;
}

function adminTasksRoutes() {
  const tasks = AdminPage.Tasks;

  return <Route path={tasks} element={<Tasks />} />;
}

function adminRolesRoutes() {
  const roles = AdminPage.Roles;

  return <Route path={roles} element={<Roles />} />;
}

function adminHooksRoutes() {
  const hooks = AdminPage.Hooks;

  return (
    <>
      <Route
        path={hooks}
        element={
          <ItemsLoader
            variant={hooks}
            render={(config) => (
              <ItemForm variant={hooks} config={config as HooksConfig} />
            )}
          />
        }
      />
      <Route
        path={`${hooks}/:item`}
        element={
          <ItemsLoader
            variant={hooks}
            render={(config) => (
              <ItemForm variant={hooks} config={config as HooksConfig} />
            )}
          />
        }
      />
    </>
  );
}

function adminScheduleRoutes() {
  const schedule = AdminPage.Schedule;

  return (
    <>
      <Route
        path={schedule}
        element={
          <ItemsLoader
            variant={schedule}
            render={(config) => (
              <ItemForm
                variant={schedule}
                config={config as ScheduleConfig}
                showTask
              />
            )}
          />
        }
      />
      <Route
        path={`${schedule}/:item`}
        element={
          <ItemsLoader
            variant={schedule}
            render={(config) => (
              <ItemForm
                variant={schedule}
                config={config as ScheduleConfig}
                showTask
              />
            )}
          />
        }
      />
    </>
  );
}

function adminAutomationsRoutes() {
  const automations = AdminPage.Automations;

  return (
    <>
      <Route
        path={automations}
        element={
          <ItemsLoader
            variant={automations}
            render={(config) => (
              <ItemForm
                variant={automations}
                config={config as AutomationConfig}
              />
            )}
          />
        }
      />
      <Route
        path={`${automations}/:item`}
        element={
          <ItemsLoader
            variant={automations}
            render={(config) => (
              <ItemForm
                variant={automations}
                config={config as AutomationConfig}
              />
            )}
          />
        }
      />
    </>
  );
}

function adminToolsRoutes() {
  const tools = AdminPage.Tools;

  return (
    <>
      <Route
        path={tools}
        element={
          <ItemsLoader
            variant={tools}
            render={(config) => (
              <ItemForm variant={tools} config={config as ToolConfig} />
            )}
          />
        }
      />
      <Route
        path={`${tools}/:item`}
        element={
          <ItemsLoader
            variant={tools}
            render={(config) => (
              <ItemForm variant={tools} config={config as ToolConfig} />
            )}
          />
        }
      />
    </>
  );
}

function adminExtRoutes() {
  const ext = AdminPage.Ext;

  return (
    <>
      <Route
        path={ext}
        element={
          <ItemsLoader
            variant={ext}
            render={(config) => (
              <ItemForm variant={ext} config={config as ExtConfig} />
            )}
          />
        }
      />
      <Route
        path={`${ext}/:item`}
        element={
          <ItemsLoader
            variant={ext}
            render={(config) => (
              <ItemForm variant={ext} config={config as ExtConfig} />
            )}
          />
        }
      />
    </>
  );
}

function adminApprovalsRoutes() {
  const approvals = AdminPage.Approvals;

  return (
    <>
      <Route path={approvals} element={<Approvals />} />
    </>
  );
}

function adminUsersRoutes() {
  const users = AdminPage.Users;

  return <Route path={users} element={<Users />} />;
}

function defaultRoutes(element: ReactNode) {
  return (
    <>
      {indexRoute(element)}
      {wildcardRoute(element)}
    </>
  );
}

function indexRoute(element: ReactNode) {
  return <Route index element={element} />;
}

function wildcardRoute(element: ReactNode) {
  return <Route path="*" element={element} />;
}
